import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  LinearProgress,
  Avatar,
  Stack,
  Divider,
  Alert,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  SmartToy as AIIcon,
  Speed as SpeedIcon,
  AttachMoney as CostIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
  Memory as TokenIcon,
  Psychology as BrainIcon,
  Timer as TimerIcon,
  Analytics as AnalyticsIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar
} from 'recharts';

function AIMetricsCard({ data, detailed = false }) {
  const [viewMode, setViewMode] = useState('overview');

  if (!data) {
    return (
      <Alert severity="info">
        Metriche AI non disponibili
      </Alert>
    );
  }

  // Extract metrics with defaults
  const metrics = {
    totalResponses: data.total_ai_responses || 0,
    avgResponseTime: data.avg_response_time || 0,
    estimatedCost: data.estimated_cost || 0,
    tokensUsed: data.tokens_used || 0,
    successRate: data.success_rate || 0,
    errorRate: data.error_rate || 0,
    modelUsage: data.model_usage || {},
    responseTimeHistory: data.response_time_history || [],
    costHistory: data.cost_history || [],
    dailyMetrics: data.daily_metrics || []
  };

  // Calculate performance indicators
  const getPerformanceLevel = (responseTime) => {
    if (responseTime < 2) return { label: 'Eccellente', color: 'success', value: 100 };
    if (responseTime < 5) return { label: 'Buona', color: 'success', value: 80 };
    if (responseTime < 10) return { label: 'Media', color: 'warning', value: 60 };
    return { label: 'Lenta', color: 'error', value: 40 };
  };

  const getCostEfficiency = (cost) => {
    if (cost < 0.01) return { label: 'Molto Efficiente', color: 'success' };
    if (cost < 0.05) return { label: 'Efficiente', color: 'success' };
    if (cost < 0.1) return { label: 'Moderata', color: 'warning' };
    return { label: 'Costosa', color: 'error' };
  };

  const performance = getPerformanceLevel(metrics.avgResponseTime);
  const costEfficiency = getCostEfficiency(metrics.estimatedCost);

  // Main metrics cards
  const mainMetrics = [
    {
      title: 'Risposte Generate',
      value: metrics.totalResponses.toLocaleString(),
      icon: <AIIcon />,
      color: '#2196f3',
      description: 'Totale risposte AI generate',
      trend: data.response_trend || 0
    },
    {
      title: 'Tempo Medio',
      value: `${metrics.avgResponseTime.toFixed(2)}s`,
      icon: <TimerIcon />,
      color: performance.color === 'success' ? '#4caf50' : performance.color === 'warning' ? '#ff9800' : '#f44336',
      description: 'Tempo medio di risposta',
      performance: performance
    },
    {
      title: 'Costo Stimato',
      value: `$${metrics.estimatedCost.toFixed(4)}`,
      icon: <CostIcon />,
      color: '#9c27b0',
      description: 'Costo totale stimato',
      efficiency: costEfficiency
    },
    {
      title: 'Token Utilizzati',
      value: metrics.tokensUsed.toLocaleString(),
      icon: <TokenIcon />,
      color: '#ff5722',
      description: 'Token totali processati'
    }
  ];

  // Model usage data for charts
  const modelUsageData = Object.entries(metrics.modelUsage).map(([model, usage]) => ({
    name: model,
    value: usage,
    percentage: ((usage / metrics.totalResponses) * 100).toFixed(1)
  }));

  const renderCustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: 0, color: entry.color }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6" component="h2">
            <BrainIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
            Metriche Performance AI
          </Typography>
          
          {detailed && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="overview">Panoramica</ToggleButton>
              <ToggleButton value="performance">Performance</ToggleButton>
              <ToggleButton value="costs">Costi</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {/* Main Metrics Grid */}
        <Grid container spacing={3} mb={3}>
          {mainMetrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${metric.color}20 0%, ${metric.color}10 100%)`,
                  border: `1px solid ${metric.color}30`
                }}
              >
                <CardContent sx={{ textAlign: 'center', pb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: metric.color,
                      width: 48,
                      height: 48,
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    {metric.icon}
                  </Avatar>

                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {metric.value}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {metric.title}
                  </Typography>

                  {metric.performance && (
                    <Chip
                      label={metric.performance.label}
                      size="small"
                      color={metric.performance.color}
                      sx={{ mb: 1 }}
                    />
                  )}

                  {metric.efficiency && (
                    <Chip
                      label={metric.efficiency.label}
                      size="small"
                      color={metric.efficiency.color}
                      sx={{ mb: 1 }}
                    />
                  )}

                  {metric.trend !== undefined && metric.trend !== 0 && (
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                      {metric.trend > 0 ? (
                        <TrendUpIcon color="success" fontSize="small" />
                      ) : (
                        <TrendDownIcon color="error" fontSize="small" />
                      )}
                      <Typography
                        variant="caption"
                        sx={{ color: metric.trend > 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}
                      >
                        {metric.trend > 0 ? '+' : ''}{metric.trend.toFixed(1)}%
                      </Typography>
                    </Stack>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {metric.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Detailed Views */}
        {detailed && viewMode === 'performance' && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Storico Performance
            </Typography>
            
            {metrics.responseTimeHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={metrics.responseTimeHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="timestamp" tickFormatter={(value) => new Date(value).toLocaleDateString()} />
                  <YAxis />
                  <RechartsTooltip content={renderCustomTooltip} />
                  <Area
                    type="monotone"
                    dataKey="response_time"
                    stroke="#2196f3"
                    fill="#2196f3"
                    fillOpacity={0.3}
                    name="Tempo Risposta (s)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Alert severity="info">Dati storici performance non disponibili</Alert>
            )}

            {/* Performance Indicators */}
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Livello Performance Attuale
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={performance.value}
                    color={performance.color}
                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {performance.value}% - {performance.label}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Tasso di Successo
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={metrics.successRate}
                    color="success"
                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {metrics.successRate.toFixed(1)}% richieste completate con successo
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {detailed && viewMode === 'costs' && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Analisi Costi
            </Typography>
            
            {metrics.costHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={metrics.costHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="timestamp" tickFormatter={(value) => new Date(value).toLocaleDateString()} />
                  <YAxis />
                  <RechartsTooltip content={renderCustomTooltip} />
                  <Line
                    type="monotone"
                    dataKey="cost"
                    stroke="#9c27b0"
                    strokeWidth={2}
                    dot={{ fill: '#9c27b0', strokeWidth: 2, r: 4 }}
                    name="Costo ($)"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Alert severity="info">Storico costi non disponibile</Alert>
            )}

            {/* Cost Breakdown */}
            <Grid container spacing={2} mt={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Efficienza Costi
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={costEfficiency.label}
                    color={costEfficiency.color}
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    ${(metrics.estimatedCost / Math.max(metrics.totalResponses, 1)).toFixed(6)} per risposta
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Token per Risposta
                </Typography>
                <Typography variant="h6" color="primary">
                  {Math.round(metrics.tokensUsed / Math.max(metrics.totalResponses, 1))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  token medi per interazione
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {detailed && viewMode === 'overview' && modelUsageData.length > 0 && (
          <Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Utilizzo Modelli AI
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={modelUsageData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <RechartsTooltip
                  formatter={(value, name) => [
                    `${value} richieste (${modelUsageData.find(d => d.value === value)?.percentage}%)`,
                    'Utilizzo'
                  ]}
                />
                <Bar dataKey="value" fill="#2196f3" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* Quality Indicators */}
        <Box mt={3}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Indicatori di Qualità
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="success.main">
                  {metrics.successRate.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tasso Successo
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="error.main">
                  {metrics.errorRate.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tasso Errori
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary.main">
                  {(metrics.totalResponses / Math.max(Object.keys(metrics.modelUsage).length, 1)).toFixed(0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Risposte per Modello
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Help Info */}
        <Box mt={2} display="flex" alignItems="center" justifyContent="center">
          <Tooltip title="Le metriche AI includono tempo di risposta, costi, utilizzo token e performance dei modelli">
            <IconButton size="small">
              <InfoIcon color="action" fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" ml={1}>
            Ultimo aggiornamento: {new Date().toLocaleTimeString()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default AIMetricsCard;
